import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { Plus, Edit, Trash2, Play, Wand2, Zap, RefreshCw, Sparkles, Pause, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';
import BankingFlowRecap from '@/components/common/BankingFlowRecap';
import GettingStartedChecklist from '@/components/common/GettingStartedChecklist';
import { BankingRulesTour } from '@/components/banking/BankingRulesTour';
import { Sparkline } from '@/components/common/Sparkline';
import { SmartGroupRulesPanel } from '@/components/banking/SmartGroupRulesPanel';
import { localToday } from '@/lib/dateRange';

const MATCH_TYPES = [
  { v: 'contains', l: 'Contains' },
  { v: 'starts_with', l: 'Starts with' },
  { v: 'exact', l: 'Exact match' },
  { v: 'regex', l: 'Regex' },
];
const KINDS = [
  { v: 'expense', l: 'Create Expense', entityType: 'expense_category', entityLabel: 'Expense category' },
  { v: 'supplier_payment', l: 'Supplier Payment (review)', entityType: 'supplier', entityLabel: 'Supplier' },
  { v: 'customer_receipt', l: 'Customer Receipt (review)', entityType: 'customer', entityLabel: 'Customer' },
  { v: 'employee_payment', l: 'Employee Payment (salary / advance)', entityType: 'employee', entityLabel: 'Employee' },
  { v: 'partner_payment', l: 'Partner Draw / Payment', entityType: 'partner', entityLabel: 'Partner' },
  { v: 'platform_settlement', l: 'Online Platform Settlement', entityType: 'platform', entityLabel: 'Platform' },
  { v: 'incoming_other', l: 'Incoming Other (review)', entityType: null, entityLabel: 'Notes / tag' },
];
const KIND_BY_VALUE = Object.fromEntries(KINDS.map((k) => [k.v, k]));

const blank = {
  name: '', match_type: 'contains', match_value: '', direction: 'outgoing',
  amount_min: '', amount_max: '', kind: 'expense', category: '', vendor_name: '',
  branch_id: '', priority: 100, enabled: true,
};

/**
 * Banking Auto-Rules Engine.
 *
 * Lets users define rules like "every bank txn whose description contains
 * 'STC' is a Telecom expense" so monthly reconciliation runs unattended.
 *
 * Each rule has a `Test` button that dry-runs against the last 30 days
 * and shows how many rows would have matched, plus 20 sample descriptions
 * — invaluable for tuning match_value before saving.
 *
 * The page-level `Replay & apply` button retroactively categorises old
 * uncategorised txns once the user is happy with their ruleset.
 */
export default function BankingRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [testResult, setTestResult] = useState(null);
  const [replayResult, setReplayResult] = useState(null);
  // iter421 — Smart Category dropdown: cache entity-options per kind so
  // switching between Supplier / Employee / Partner / etc. populates
  // the right dropdown without a re-fetch each time.
  const [entityOptionsByKind, setEntityOptionsByKind] = useState({});

  // Suggest-from-history modal state. The backend endpoint clusters the
  // last 90 days of un-matched bank txns by their first significant
  // alpha token and proposes high-confidence rules; the user just ticks
  // the ones they want and (optionally) edits the category before
  // bulk-creating them.
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestMeta, setSuggestMeta] = useState(null);
  const [selectedSuggestions, setSelectedSuggestions] = useState({});

  // Per-rule timeline cache (rule_id → {series, total, max_day}). We
  // fetch lazily on first render of each row's sparkline so a 100-rule
  // table doesn't hit the backend for everything up-front.
  const [timelines, setTimelines] = useState({});

  // Top movers: rules whose 7-day hit count grew or shrank the most
  // vs the prior 7 days. Surfaces broken / newly-active rules at the
  // top of the page so users notice them without scrolling.
  const [movers, setMovers] = useState([]);

  // Guided tour (5-step walkthrough modal) — opens from the info callout.
  const [tourOpen, setTourOpen] = useState(false);

  // iter395 — Search filter for the rules table. Filters across name,
  // match_value, category, and vendor_name so users with 50+ rules can
  // quickly find the one they want without scrolling.
  const [search, setSearch] = useState('');

  useEffect(() => { fetchRules(); fetchMovers(); }, []);

  // Whenever the rules list changes (load, create, delete), prefetch
  // the timeline for any rule that has hits but no cached series. This
  // is cheap because the data already lives on the rule doc — the
  // timeline endpoint only zero-fills and reshapes it.
  useEffect(() => {
    rules.forEach((r) => {
      if ((r.hits_count || 0) > 0 && !timelines[r.id]) {
        api.get(`/banking/rules/${r.id}/timeline?days=30`)
          .then((res) => setTimelines((t) => ({ ...t, [r.id]: res.data })))
          .catch(() => { /* sparkline just won't render — non-blocking */ });
      }
    });
    // Intentionally only depend on rules.length + ids — rerunning on
    // every keystroke would be wasteful.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rules.map((r) => r.id).join(',')]);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const r = await api.get('/banking/rules');
      setRules(r.data.rules || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load rules'); }
    finally { setLoading(false); }
  };

  // Top movers — silently fail if the endpoint errors so the page
  // still works for users on older backend versions.
  const fetchMovers = async () => {
    try {
      const r = await api.get('/banking/rules/top-movers?window_days=7&limit=3');
      setMovers(r.data.movers || []);
    } catch { setMovers([]); }
  };

  // iter421 — When the user picks a kind in the dialog, lazily load the
  // matching entity list so the Category dropdown can show real choices
  // (Supplier list / Employee list / Partner list / etc.). Reuses the
  // /api/beneficiaries/link-options/{type} endpoint that already returns
  // [{id, label}] across all entity types.
  useEffect(() => {
    const meta = KIND_BY_VALUE[form.kind];
    if (!meta?.entityType) return;
    if (entityOptionsByKind[meta.entityType]) return; // already cached
    api.get(`/beneficiaries/link-options/${meta.entityType}`)
      .then((r) => setEntityOptionsByKind((prev) => ({ ...prev, [meta.entityType]: r.data || [] })))
      .catch(() => setEntityOptionsByKind((prev) => ({ ...prev, [meta.entityType]: [] })));
  }, [form.kind, entityOptionsByKind]);

  const openCreate = () => { setEditing(null); setForm(blank); setTestResult(null); setShowDialog(true); };
  const openEdit = (rule) => {
    setEditing(rule);
    setForm({ ...blank, ...rule, amount_min: rule.amount_min ?? '', amount_max: rule.amount_max ?? '' });
    setTestResult(null);
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.match_value.trim()) { toast.error('Name and match value are required'); return; }
    const payload = {
      ...form,
      amount_min: form.amount_min === '' ? null : Number(form.amount_min),
      amount_max: form.amount_max === '' ? null : Number(form.amount_max),
      priority: Number(form.priority) || 100,
    };
    try {
      if (editing) await api.put(`/banking/rules/${editing.id}`, payload);
      else await api.post('/banking/rules', payload);
      toast.success(editing ? 'Rule updated' : 'Rule created');
      setShowDialog(false); fetchRules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"? Existing categorised entries will not be affected.`)) return;
    try {
      await api.delete(`/banking/rules/${rule.id}`);
      toast.success('Rule deleted'); fetchRules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  // Pause a rule for 30 days — keeps hit history but stops it firing
  // (replay loop and live matcher both skip it). Useful for seasonal
  // vendors like AC servicing that only matter in summer.
  const pause = async (rule) => {
    try {
      await api.post(`/banking/rules/${rule.id}/pause?days=30`);
      toast.success(`Paused "${rule.name}" for 30 days`);
      fetchRules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Pause failed'); }
  };
  const resume = async (rule) => {
    try {
      await api.post(`/banking/rules/${rule.id}/resume`);
      toast.success(`Resumed "${rule.name}"`);
      fetchRules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Resume failed'); }
  };

  // Test the rule that's currently in the form (saved or unsaved). The
  // dedicated endpoint accepts an unsaved payload so users can iterate
  // on `match_value` and immediately see what would match — a tight
  // feedback loop before they commit a rule.
  const testForm = async () => {
    if (!form.match_value.trim()) { toast.error('Enter a match value first'); return; }
    try {
      const payload = {
        ...form,
        amount_min: form.amount_min === '' ? null : Number(form.amount_min),
        amount_max: form.amount_max === '' ? null : Number(form.amount_max),
      };
      const r = await api.post('/banking/rules/test-preview', payload);
      setTestResult(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
  };

  const replayDryRun = async () => {
    try { const r = await api.post('/banking/rules/replay?days=30&apply=false'); setReplayResult(r.data); toast.success(`Dry run: ${r.data.matched} txns would match`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Replay failed'); }
  };
  const replayApply = async () => {
    if (!window.confirm('Apply rules to last 30 days? This will create expense records for all matching outgoing txns.')) return;
    try { const r = await api.post('/banking/rules/replay?days=30&apply=true'); setReplayResult(r.data); toast.success(`Applied: ${r.data.applied} expenses created`); fetchRules(); fetchMovers(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
  };

  // Open the suggestions modal and fetch a fresh batch from the backend.
  const openSuggest = async () => {
    setShowSuggest(true);
    setSuggestLoading(true);
    setSuggestions([]);
    setSuggestMeta(null);
    setSelectedSuggestions({});
    try {
      const r = await api.post('/banking/rules/suggest?days=90&min_count=3&limit=15');
      setSuggestions(r.data.suggestions || []);
      setSuggestMeta({ scanned: r.data.scanned, window_days: r.data.window_days });
      // Tick everything by default — the user usually wants the lot.
      const init = {};
      (r.data.suggestions || []).forEach((s) => { init[s.token] = { picked: true, category: s.suggested_category || '' }; });
      setSelectedSuggestions(init);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate suggestions');
    } finally {
      setSuggestLoading(false);
    }
  };

  const toggleSuggestion = (token) => {
    setSelectedSuggestions((prev) => ({
      ...prev,
      [token]: { ...(prev[token] || { category: '' }), picked: !(prev[token]?.picked) },
    }));
  };

  const setSuggestionCategory = (token, category) => {
    setSelectedSuggestions((prev) => ({
      ...prev,
      [token]: { ...(prev[token] || { picked: true }), category },
    }));
  };

  const acceptSuggestions = async () => {
    const picked = suggestions.filter((s) => selectedSuggestions[s.token]?.picked);
    if (picked.length === 0) { toast.error('Tick at least one suggestion'); return; }
    const payload = {
      rules: picked.map((s) => ({
        name: s.suggested_name,
        match_type: 'contains',
        match_value: s.suggested_match_value,
        direction: s.direction,
        kind: 'expense',
        category: (selectedSuggestions[s.token]?.category || '').trim() || s.suggested_category || 'Uncategorized',
        priority: 100,
        enabled: true,
      })),
    };
    try {
      const r = await api.post('/banking/rules/bulk-create', payload);
      toast.success(`Created ${r.data.count} rule${r.data.count === 1 ? '' : 's'}`);
      setShowSuggest(false);
      fetchRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk create failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="banking-rules-page">
        {/* What is this page? — collapsible orientation callout. Triggers
            the 5-step walkthrough tour via "Show me around". */}
        <PageInfoCallout
          storageKey="banking-rules"
          title="What are Auto-Rules?"
          onTour={() => setTourOpen(true)}
        >
          <p>
            <strong>Teach the system once — never match that line again.</strong> Create rules like
            <em> &quot;contains STC → Telecom Expense&quot;</em> or <em>&quot;amount = 5,000 on the 1st →
            Ahmed&apos;s salary&quot;</em>. New bank lines matching the rule get auto-categorised; use
            <strong> Replay &amp; apply</strong> to back-fill historical lines too.
          </p>
          <p className="mt-2 text-xs opacity-80">
            👉 <strong>Goal:</strong> keep adding rules until your <strong>Quick-link Inbox</strong> is almost empty.
            Tap <strong>Suggest from history</strong> to seed your first rules in one click.
          </p>
        </PageInfoCallout>
        <BankingFlowRecap active="/banking-rules" />

        {/* iter498 — Smart-Group rules library (different from description-based rules below). */}
        <SmartGroupRulesPanel />

        <GettingStartedChecklist
          storageKey="banking-rules-v1"
          title="Activate Auto-Rules"
          steps={[
            { key: 'suggest', label: 'Let AI suggest rules from your 90-day history', onClick: openSuggest, cta: 'Suggest rules' },
            { key: 'review', label: 'Review & accept at least 1 suggested rule', href: '#', cta: 'I will do this' },
            { key: 'replay', label: 'Replay rules to retro-categorize the last 30 days', onClick: replayApply, cta: 'Replay' },
            { key: 'explore', label: 'Browse the live matcher in Banking Hub', href: '/banking-hub', cta: 'Open hub' },
          ]}
        />

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit flex items-center gap-2">
              <Wand2 size={20} className="text-violet-600" /> Banking Auto-Rules
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Match bank descriptions to categories automatically — &quot;STC&quot; → Telecom, &quot;Saudi Aramco&quot; → Fuel, etc.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={openSuggest} className="rounded-xl border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="rules-suggest">
              <Sparkles size={13} className="mr-1.5" /> Suggest from history
            </Button>
            <Button variant="outline" size="sm" onClick={replayDryRun} className="rounded-xl" data-testid="rules-replay-dry">
              <Play size={13} className="mr-1.5" /> Dry-run last 30 days
            </Button>
            <Button onClick={replayApply} size="sm" className="rounded-xl bg-emerald-600 hover:bg-emerald-700" data-testid="rules-replay-apply">
              <Zap size={13} className="mr-1.5" /> Replay &amp; apply
            </Button>
            <Button onClick={openCreate} className="rounded-xl bg-primary hover:bg-primary/90" data-testid="rules-new">
              <Plus size={14} className="mr-1.5" /> New rule
            </Button>
          </div>
        </div>

        {replayResult && (
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="p-3 text-xs flex items-center gap-3" data-testid="rules-replay-result">
              <Zap size={14} className="text-emerald-600" />
              <div className="flex-1">
                <strong>{replayResult.dry_run ? 'Dry run: ' : 'Applied: '}</strong>
                scanned {replayResult.scanned} txns · {replayResult.matched} matched
                {!replayResult.dry_run && ` · ${replayResult.applied} expenses created`}
                {replayResult.by_rule?.length > 0 && ` · ${replayResult.by_rule.length} rule(s) fired`}
              </div>
              <Button size="sm" variant="ghost" onClick={() => setReplayResult(null)} className="h-7 px-2 text-xs">×</Button>
            </CardContent>
          </Card>
        )}

        {/* Top movers — surface rules whose 7-day hit count grew or
            shrank the most vs the prior 7 days. Helps users notice
            broken / newly-active rules without scrolling. Hidden when
            there's nothing interesting to show. */}
        {movers.length > 0 && (
          <Card className="border-violet-200 bg-violet-50/30 dark:bg-violet-950/10" data-testid="rules-top-movers">
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-outfit flex items-center gap-2 text-violet-800 dark:text-violet-200">
                <TrendingUp size={13} /> Top movers · last 7 days vs prior 7 days
                <span className="text-[10px] font-normal text-muted-foreground ml-1" title="Rules whose fire count changed the most">
                  · spot misfiring rules early
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 pb-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {movers.map((m) => {
                  const Icon = m.direction === 'up' ? TrendingUp : m.direction === 'down' ? TrendingDown : Minus;
                  const tone = m.direction === 'up'
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : m.direction === 'down'
                      ? 'border-rose-300 bg-rose-50 text-rose-800'
                      : 'border-stone-300 bg-stone-50 text-stone-700';
                  return (
                    <div key={m.id} className={`rounded-lg border px-2.5 py-2 ${tone}`} data-testid={`mover-${m.id}`}>
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold leading-tight">
                        <Icon size={12} className="shrink-0" />
                        <span className="truncate" title={m.name}>{m.name}</span>
                      </div>
                      <div className="mt-1 text-[10px] tabular-nums opacity-90">
                        {m.previous} → <strong>{m.current}</strong> fires
                        <span className="ml-1.5 font-semibold">
                          {m.delta > 0 ? '+' : ''}{m.delta}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rules table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-outfit flex items-center gap-2 flex-wrap">
              Rules <Badge variant="outline" className="text-[10px]">{rules.length}</Badge>
              {loading && <RefreshCw size={12} className="animate-spin text-muted-foreground" />}
              {/* iter395 — Inline filter input. Uses tabular search across
                  name + match value + category + vendor so users can find
                  rules by any visible field. */}
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter rules — name, match, vendor…"
                className="h-7 ml-auto text-xs w-64 max-w-full"
                data-testid="rules-filter-input"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-[10px] text-rose-600 hover:underline"
                  data-testid="rules-filter-clear"
                >
                  Clear
                </button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="rules-table">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="px-3 py-2 font-medium">Priority</th>
                    <th className="px-3 py-2 font-medium">Name</th>
                    <th className="px-3 py-2 font-medium">Match</th>
                    <th className="px-3 py-2 font-medium">Direction</th>
                    <th className="px-3 py-2 font-medium">Action</th>
                    <th className="px-3 py-2 font-medium">Category / Vendor</th>
                    <th className="px-3 py-2 font-medium text-right">Hits</th>
                    <th className="px-3 py-2 font-medium">Last 30 days</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {rules.length === 0 && !loading && (
                    <tr><td colSpan={10} className="px-3 py-12 text-center text-muted-foreground">
                      No rules yet. Click &quot;New rule&quot; to define your first auto-categorisation.
                    </td></tr>
                  )}
                  {(() => {
                    const q = search.trim().toLowerCase();
                    const visible = q
                      ? rules.filter((r) =>
                          (r.name || '').toLowerCase().includes(q)
                          || (r.match_value || '').toLowerCase().includes(q)
                          || (r.category || '').toLowerCase().includes(q)
                          || (r.vendor_name || '').toLowerCase().includes(q)
                          || (r.kind || '').toLowerCase().includes(q),
                        )
                      : rules;
                    if (q && visible.length === 0) {
                      return (
                        <tr><td colSpan={10} className="px-3 py-8 text-center text-muted-foreground text-xs" data-testid="rules-filter-empty">
                          No rules match <strong>&quot;{search}&quot;</strong>. Try a shorter keyword.
                        </td></tr>
                      );
                    }
                    return visible.map(r => {
                    // `paused_until` is an ISO date — anything in the future
                    // means the rule is silently dormant. We surface this as
                    // a distinct visual state so users see at a glance which
                    // rules are seasonally suspended.
                    const isPaused = r.paused_until && r.paused_until > localToday();
                    const tl = timelines[r.id];
                    return (
                    <tr key={r.id} className="border-b hover:bg-muted/40" data-testid={`rule-row-${r.id}`}>
                      <td className="px-3 py-2 tabular-nums text-muted-foreground">{r.priority}</td>
                      <td className="px-3 py-2 font-semibold">{r.name}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        <span className="text-[10px] uppercase tracking-wider opacity-60">{r.match_type}</span>{' '}
                        <code className="bg-muted px-1 rounded">{r.match_value}</code>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{r.direction || 'both'}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                          r.kind === 'expense' ? 'bg-rose-100 text-rose-700' :
                          r.kind === 'supplier_payment' ? 'bg-amber-100 text-amber-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>{r.kind}</span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{r.category || r.vendor_name || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{r.hits_count || 0}</td>
                      <td className="px-3 py-2" data-testid={`rule-spark-${r.id}`}>
                        {(r.hits_count || 0) === 0 ? (
                          <span className="text-[10px] text-muted-foreground/60">No hits yet</span>
                        ) : tl ? (
                          <Sparkline series={tl.series} max={tl.max_day} total={tl.total} />
                        ) : (
                          <span className="text-[10px] text-muted-foreground/60">…</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {isPaused ? (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-100 text-amber-700" title={`Paused until ${r.paused_until}`} data-testid={`rule-paused-${r.id}`}>
                            Paused · {r.paused_until.slice(5)}
                          </span>
                        ) : (
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${r.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'}`}>
                            {r.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {/* Wrapped in a flex container so the buttons sit
                            without stray whitespace text-nodes between them
                            (root cause of the React `<span> child of <tr>`
                            hydration warning that used to spam the console). */}
                        <div className="inline-flex items-center gap-0.5">
                          {isPaused ? (
                            <Button size="sm" variant="ghost" onClick={() => resume(r)} className="h-7 w-7 p-0 text-emerald-700 hover:bg-emerald-100" title="Resume" data-testid={`resume-${r.id}`}><Play size={12} /></Button>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={() => pause(r)} className="h-7 w-7 p-0 text-amber-700 hover:bg-amber-100" title="Pause for 30 days" data-testid={`pause-${r.id}`}><Pause size={12} /></Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 p-0" title="Edit" data-testid={`edit-${r.id}`}><Edit size={12} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(r)} className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-100" title="Delete" data-testid={`del-${r.id}`}><Trash2 size={12} /></Button>
                        </div>
                      </td>
                    </tr>
                    );
                  });
                  })()}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Edit Rule' : 'New Rule'}</DialogTitle></DialogHeader>
          {/* iter422 — Smart Suggest strip. Scans the last 90 days of
              unmatched bank txns and proposes high-confidence rules.
              Click a chip to auto-fill the form below. Hidden when
              editing an existing rule (suggestions only make sense for
              new rules) and when the suggest call returns nothing. */}
          {!editing && (
            <SmartSuggestStrip
              onPick={(s) => setForm((prev) => ({
                ...prev,
                name: s.suggested_name || prev.name,
                match_type: 'contains',
                match_value: s.suggested_match_value || s.token || prev.match_value,
                direction: s.direction || prev.direction,
                // iter423 — Backend now also resolves the entity. If a
                // supplier/customer/employee name matches the token,
                // pre-select the right kind + entity in one shot.
                kind: s.suggested_kind || prev.kind,
                category: s.suggested_category && s.suggested_category !== 'Uncategorized'
                  ? s.suggested_category
                  : prev.category,
              }))}
            />
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. STC Telecom" data-testid="rule-name" />
            </div>
            <div>
              <Label>Match type</Label>
              <Select value={form.match_type} onValueChange={v => setForm({ ...form, match_type: v })}>
                <SelectTrigger data-testid="rule-match-type"><SelectValue /></SelectTrigger>
                <SelectContent>{MATCH_TYPES.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Match value</Label>
              <Input value={form.match_value} onChange={e => setForm({ ...form, match_value: e.target.value })} placeholder='e.g. STC' data-testid="rule-match-value" />
            </div>
            <div>
              <Label>Direction</Label>
              <Select value={form.direction || 'both'} onValueChange={v => setForm({ ...form, direction: v === 'both' ? null : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Both</SelectItem>
                  <SelectItem value="outgoing">Outgoing only</SelectItem>
                  <SelectItem value="incoming">Incoming only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority (lower wins)</Label>
              <Input type="number" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} />
            </div>
            <div>
              <Label>Amount min (optional)</Label>
              <Input type="number" value={form.amount_min} onChange={e => setForm({ ...form, amount_min: e.target.value })} placeholder="—" />
            </div>
            <div>
              <Label>Amount max (optional)</Label>
              <Input type="number" value={form.amount_max} onChange={e => setForm({ ...form, amount_max: e.target.value })} placeholder="—" />
            </div>
            <div>
              <Label>Action kind</Label>
              <Select value={form.kind} onValueChange={v => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="rule-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map(k => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{KIND_BY_VALUE[form.kind]?.entityLabel || 'Category'}</Label>
              {KIND_BY_VALUE[form.kind]?.entityType ? (
                <Select
                  value={form.category || 'free'}
                  onValueChange={(v) => setForm({ ...form, category: v === 'free' ? '' : v })}
                >
                  <SelectTrigger data-testid="rule-category-select">
                    <SelectValue placeholder="Pick one…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">— None / type below —</SelectItem>
                    {(entityOptionsByKind[KIND_BY_VALUE[form.kind].entityType] || []).map((o) => (
                      <SelectItem key={o.id} value={o.label || o.id}>{o.label || o.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={form.category || ''}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="e.g. Telecom"
                  data-testid="rule-category-input"
                />
              )}
              {/* Free-text fallback when entity-typed kinds need a custom value
                  (e.g., a brand-new supplier not yet added). The Select above
                  only sets `category` when an option is picked; user can clear
                  by selecting "None" and typing here. */}
              {KIND_BY_VALUE[form.kind]?.entityType && (
                <Input
                  value={form.category || ''}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="…or type a custom name"
                  className="mt-1.5 text-xs"
                  data-testid="rule-category-custom"
                />
              )}
            </div>
            <div>
              <Label>Vendor name (optional)</Label>
              <Input value={form.vendor_name || ''} onChange={e => setForm({ ...form, vendor_name: e.target.value })} placeholder="STC" />
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={v => setForm({ ...form, enabled: v })} />
              <Label className="cursor-pointer">Enabled</Label>
            </div>
          </div>

          {/* Preview button — instantly shows what the unsaved rule would
              match in the last 30 days so users can tune match_value
              without saving and replaying. */}
          <div className="rounded-xl bg-violet-50 dark:bg-violet-950/10 border border-violet-200 p-3 mt-2">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs">
                <strong className="text-violet-800">Test this rule</strong>
                <p className="text-violet-700/70 mt-0.5">Dry-run against the last 30 days to see what would match.</p>
              </div>
              <Button size="sm" variant="outline" onClick={testForm} className="border-violet-300" data-testid="rule-test-btn">
                <Play size={12} className="mr-1.5" /> Test
              </Button>
            </div>
            {testResult && (
              <div className="mt-2 text-xs" data-testid="rule-test-result">
                <p className="font-semibold">{testResult.matched} of {testResult.scanned} txns matched.</p>
                {testResult.samples?.length > 0 && (
                  <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                    {testResult.samples.map((s, i) => (
                      <div key={i} className="font-mono text-[10px] bg-white/60 px-2 py-0.5 rounded truncate">
                        {s.date} · {s.direction} · {s.amount} · {s.description?.slice(0, 80)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="rule-save">
              {editing ? 'Update' : 'Create'} rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suggest-from-history modal — clusters the last 90 days of
          un-matched bank txns by their first significant token and
          proposes auto-rules. The user ticks what they want, optionally
          edits the category, and clicks "Create selected". */}
      <Dialog open={showSuggest} onOpenChange={setShowSuggest}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={16} className="text-violet-600" /> Suggested rules from your history
            </DialogTitle>
            <DialogDescription>
              Clusters un-matched bank lines from the last 90 days, then proposes one auto-rule per cluster. Tick the ones you want, edit the category, and click Create.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-muted-foreground" data-testid="rules-suggest-meta">
            {suggestLoading
              ? 'Scanning the last 90 days…'
              : suggestMeta
                ? `Scanned ${suggestMeta.scanned} bank transactions over ${suggestMeta.window_days} days. ${suggestions.length} cluster${suggestions.length === 1 ? '' : 's'} found that aren't already covered by your rules.`
                : 'Click "Suggest from history" to scan.'}
          </div>

          <div className="max-h-[400px] overflow-y-auto rounded-lg border" data-testid="rules-suggest-list">
            {!suggestLoading && suggestions.length === 0 && (
              <div className="p-6 text-center text-xs text-muted-foreground">
                No new clusters found. Either you've already covered the recurring lines, or there aren't enough bank transactions yet (need at least 3 hits per token over the last 90 days).
              </div>
            )}
            {suggestions.map((s) => {
              const sel = selectedSuggestions[s.token] || { picked: true, category: '' };
              // Confidence colour-codes the suggestion strength so users
              // can scan the list and tick the obvious wins first.
              const conf = s.confidence ?? 0;
              const confTone = conf >= 80 ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                : conf >= 60 ? 'bg-sky-100 text-sky-700 border-sky-300'
                : 'bg-stone-100 text-stone-700 border-stone-300';
              return (
                <div key={s.token} className={`px-3 py-2.5 border-b last:border-b-0 ${sel.picked ? 'bg-violet-50/40' : ''}`} data-testid={`suggest-row-${s.token}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={!!sel.picked}
                      onChange={() => toggleSuggestion(s.token)}
                      className="mt-1 cursor-pointer"
                      data-testid={`suggest-pick-${s.token}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <code className="bg-violet-100 text-violet-800 px-1.5 py-0.5 rounded text-[11px] font-semibold">
                          contains &quot;{s.token}&quot;
                        </code>
                        <Badge variant="outline" className="text-[10px]">{s.direction}</Badge>
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${confTone}`} data-testid={`suggest-confidence-${s.token}`}>
                          {conf}% confidence
                        </span>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {s.count}× · total {s.total_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        {s.merged_variants?.length > 0 && (
                          <span className="text-[10px] text-violet-600/80" title={`Merged variants: ${s.merged_variants.join(', ')}`}>
                            +{s.merged_variants.length} variant{s.merged_variants.length === 1 ? '' : 's'} merged
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <Label className="text-[10px] text-muted-foreground shrink-0">Category</Label>
                        <Input
                          value={sel.category || ''}
                          onChange={(e) => setSuggestionCategory(s.token, e.target.value)}
                          placeholder="e.g. Telecom"
                          className="h-7 text-xs"
                          data-testid={`suggest-cat-${s.token}`}
                        />
                      </div>
                      {s.samples?.length > 0 && (
                        <div className="mt-1.5 space-y-0.5">
                          {s.samples.slice(0, 3).map((sm, i) => (
                            <div key={i} className="font-mono text-[10px] text-muted-foreground/80 truncate">
                              {sm.date} · {sm.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} · {sm.description?.slice(0, 80)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuggest(false)}>Cancel</Button>
            <Button
              onClick={acceptSuggestions}
              disabled={suggestLoading || suggestions.length === 0 || !Object.values(selectedSuggestions).some(v => v.picked)}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="rules-suggest-accept"
            >
              <Sparkles size={13} className="mr-1.5" /> Create selected rules
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 5-step guided walkthrough — opened from the BankingInfoCallout
          "Show me around" button. The "Try it now" finish-button hooks
          straight into the Suggest dialog so users land on the most
          common starting flow. */}
      <BankingRulesTour
        open={tourOpen}
        onOpenChange={setTourOpen}
        onJumpToSuggest={openSuggest}
      />
    </DashboardLayout>
  );
}

// iter422 — Inline Smart Suggest strip. Lazily fetches /banking/rules/suggest
// on first render so opening the dialog doesn't block. Renders a row of
// clickable chips; the parent's onPick callback receives the full
// suggestion object so it can populate the rule form.
function SmartSuggestStrip({ onPick }) {
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post('/banking/rules/suggest', null, {
          params: { days: 90, min_count: 2, limit: 8 },
        });
        if (cancelled) return;
        setSuggestions(r.data?.suggestions || r.data || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load suggestions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2" data-testid="smart-suggest-strip-loading">
        <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
        Smart Suggest is scanning the last 90 days of unmatched bank txns…
      </div>
    );
  }
  if (error || !suggestions.length) return null;

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50/60 px-3 py-2.5 space-y-2" data-testid="smart-suggest-strip">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
          <span aria-hidden>💡</span> Smart Suggest — based on your recent bank activity
        </div>
        <span className="text-[10px] text-amber-700">{suggestions.length} found</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {suggestions.slice(0, 8).map((s) => (
          <button
            key={s.canonical || s.token}
            type="button"
            onClick={() => onPick(s)}
            data-testid={`suggest-chip-${(s.token || s.canonical || '').toLowerCase()}`}
            className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-md text-[11px] bg-white border border-amber-300 hover:bg-amber-100 hover:border-amber-400 text-amber-900 transition-colors"
            title={`${s.count}× • total ${(s.total_amount || 0).toLocaleString('en', { minimumFractionDigits: 2 })} • ${s.direction || 'both'}${s.merged_variants?.length ? `\nVariants: ${s.merged_variants.join(', ')}` : ''}`}
          >
            <span className="font-semibold">{s.token || s.canonical}</span>
            <span className="text-[9px] text-amber-700">×{s.count}</span>
          </button>
        ))}
      </div>
      <div className="text-[10px] text-amber-700/80 leading-tight">
        Click a chip to auto-fill the form. We pre-set Match type=contains, the value, direction, and a category guess.
      </div>
    </div>
  );
}

